import { useEffect, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import Select from 'react-select';
import useModal from '../../../hooks/use-close-modal.hook';
import { RoutesService } from '../../../services/routes.service';
import { DialplanService } from '../../../services/dialplan.service';
import {
  MATCH_OP_VALUES,
  PROTO_VALUES,
  type ColumnView,
  type DialplanUpsert,
  type EntranteUpsert,
  type OpensipsReloadResult,
  type PreviewResult,
  type SalienteUpsert,
  type WriteResult
} from '../../../services/opensips.types';
import { AlertService } from '../../../services/alert.service';
import LogData from '../../../components/logs/log-data.component';
import InvalidFeedback from '../../../components/forms/invalid-feedback.component';
import Button from '../../../components/forms/button.component';
import Title from '../../../components/forms/titles.component';
import Input from '../../../components/forms/input.component';
import TexArea from '../../../components/forms/text-area.component';
import Icon from '../../../components/forms/icon.component';
import { OpensipsPreview, ReloadBanner } from './opensips-preview.component';

export type OpensipsKind = 'saliente' | 'entrante' | 'dialplan';

interface Props {
  kind: OpensipsKind;
  show: boolean;
  onRequestClose: (refresh: boolean) => void;
  /** DTO crudo del backend (row.raw). null = crear. */
  payload: any | null;
  duplicate?: boolean;
}

const META: Record<
  OpensipsKind,
  { table: string; reloadCmd: string; title: string; titleNew: string; help: string }
> = {
  saliente: {
    table: 'load_balancer',
    reloadCmd: 'lb_reload',
    title: 'Editar salida (gateway)',
    titleNew: 'Nueva salida (gateway)',
    help: 'Destino al que OpenSIPS balancea las llamadas salientes del Blaster (Digitalk, etc.). La capacidad por recurso limita canales simultáneos; 0 en todos = inactivo.'
  },
  entrante: {
    table: 'address',
    reloadCmd: 'address_reload',
    title: 'Editar entrada (IP permitida)',
    titleNew: 'Nueva entrada (IP permitida)',
    help: 'IP/red autorizada a mandar tráfico a OpenSIPS (los FS, carriers de entrada). grp agrupa permisos; port 0 = cualquiera.'
  },
  dialplan: {
    table: 'dialplan',
    reloadCmd: 'dp_reload',
    title: 'Editar regla de dialplan',
    titleNew: 'Nueva regla de dialplan',
    help: 'Regla de normalización de números. dpid agrupa reglas, pr es la prioridad (menor gana), match_exp se evalúa como equal o regex y subst/repl transforman el número.'
  }
};

// ---------------------------------------------------------------------------
// Form values (strings para los números, se convierten al construir el body)
// ---------------------------------------------------------------------------
interface FormValues {
  // saliente
  groupId: string;
  dstUri: string;
  resources: { resource: string; capacity: string }[];
  probeMode: string;
  maxChannels: string;
  // entrante
  grp: string;
  ip: string;
  mask: string;
  port: string;
  proto: string;
  pattern: string;
  contextInfo: string;
  // dialplan
  dpid: string;
  pr: string;
  matchOp: string;
  matchExp: string;
  matchFlags: string;
  substExp: string;
  replExp: string;
  timerec: string;
  enabled: boolean;
  attrs: string;
  // común
  description: string;
}

const EMPTY: FormValues = {
  groupId: '',
  dstUri: 'sip:',
  resources: [{ resource: 'pstn', capacity: '' }],
  probeMode: '0',
  maxChannels: '',
  grp: '0',
  ip: '',
  mask: '32',
  port: '0',
  proto: 'any',
  pattern: '',
  contextInfo: '',
  dpid: '',
  pr: '0',
  matchOp: 'regex',
  matchExp: '',
  matchFlags: '0',
  substExp: '',
  replExp: '',
  timerec: '',
  enabled: true,
  attrs: '',
  description: ''
};

const s = (v: unknown, d = '') => (v === undefined || v === null ? d : String(v));

function fromPayload(kind: OpensipsKind, p: any): FormValues {
  const f: FormValues = { ...EMPTY, description: s(p?.description) };
  if (kind === 'saliente') {
    f.groupId = s(p?.groupId);
    f.dstUri = s(p?.dstUri, 'sip:');
    f.resources =
      Array.isArray(p?.resources) && p.resources.length
        ? p.resources.map((r: any) => ({ resource: s(r.resource), capacity: s(r.capacity) }))
        : EMPTY.resources;
    f.probeMode = s(p?.probeMode, '0');
    f.maxChannels = s(p?.maxChannels);
  } else if (kind === 'entrante') {
    f.grp = s(p?.grp, '0');
    f.ip = s(p?.ip);
    f.mask = s(p?.mask, '32');
    f.port = s(p?.port, '0');
    f.proto = s(p?.proto, 'any');
    f.pattern = s(p?.pattern);
    f.contextInfo = s(p?.contextInfo);
  } else {
    f.dpid = s(p?.dpid);
    f.pr = s(p?.pr, '0');
    f.matchOp = s(p?.matchOp, 'regex');
    f.matchExp = s(p?.matchExp);
    f.matchFlags = s(p?.matchFlags, '0');
    f.substExp = s(p?.substExp);
    f.replExp = s(p?.replExp);
    f.timerec = s(p?.timerec);
    f.enabled = p?.enabled ?? true;
    f.attrs = s(p?.attrs);
  }
  return f;
}

const nullable = (v: string) => (v.trim() === '' ? null : v.trim());
const optNum = (v: string) => (v.trim() === '' ? null : Number(v));

function toBody(
  kind: OpensipsKind,
  f: FormValues
): SalienteUpsert | EntranteUpsert | DialplanUpsert {
  if (kind === 'saliente') {
    const body: SalienteUpsert = {
      groupId: Number(f.groupId),
      dstUri: f.dstUri.trim(),
      resources: f.resources
        .filter((r) => r.resource.trim() !== '')
        .map((r) => ({ resource: r.resource.trim(), capacity: Number(r.capacity || 0) })),
      probeMode: Number(f.probeMode),
      description: nullable(f.description),
      maxChannels: optNum(f.maxChannels)
    };
    return body;
  }
  if (kind === 'entrante') {
    const body: EntranteUpsert = {
      grp: Number(f.grp),
      ip: f.ip.trim(),
      mask: Number(f.mask),
      port: Number(f.port),
      proto: f.proto as EntranteUpsert['proto'],
      pattern: nullable(f.pattern),
      contextInfo: nullable(f.contextInfo),
      description: nullable(f.description)
    };
    return body;
  }
  const body: DialplanUpsert = {
    dpid: Number(f.dpid),
    pr: Number(f.pr),
    matchOp: f.matchOp as DialplanUpsert['matchOp'],
    matchExp: f.matchExp.trim(),
    matchFlags: Number(f.matchFlags),
    substExp: nullable(f.substExp),
    replExp: nullable(f.replExp),
    timerec: nullable(f.timerec),
    enabled: Boolean(f.enabled),
    attrs: nullable(f.attrs),
    description: nullable(f.description)
  };
  return body;
}

const api = {
  saliente: {
    previewCreate: (b: any) => RoutesService.previewCreateOutgoing(b),
    previewUpdate: (id: number, b: any) => RoutesService.previewUpdateOutgoing(id, b),
    create: (b: any) => RoutesService.createOutgoing(b),
    update: (id: number, b: any) => RoutesService.updateOutgoing(id, b)
  },
  entrante: {
    previewCreate: (b: any) => RoutesService.previewCreateIncoming(b),
    previewUpdate: (id: number, b: any) => RoutesService.previewUpdateIncoming(id, b),
    create: (b: any) => RoutesService.createIncoming(b),
    update: (id: number, b: any) => RoutesService.updateIncoming(id, b)
  },
  dialplan: {
    previewCreate: (b: any) => DialplanService.previewCreate(b),
    previewUpdate: (id: number, b: any) => DialplanService.previewUpdate(id, b),
    create: (b: any) => DialplanService.create(b),
    update: (id: number, b: any) => DialplanService.update(id, b)
  }
};

const intRule = (min: number, max: number, label: string, required = true) => ({
  validate: (v: string) => {
    if (String(v ?? '').trim() === '') return required ? `${label} es requerido` : true;
    const n = Number(v);
    if (!Number.isInteger(n)) return `${label} debe ser entero`;
    if (n < min || n > max) return `${label} debe estar entre ${min} y ${max}`;
    return true;
  }
});

const toOpts = (vals: readonly string[]) => vals.map((v) => ({ value: v, label: v }));
const PROTO_OPTS = toOpts(PROTO_VALUES);
const MATCH_OPTS = toOpts(MATCH_OP_VALUES);
const PROBE_OPTS = [
  { value: '0', label: '0 · Sin probing' },
  { value: '1', label: '1 · Probar solo inactivos' },
  { value: '2', label: '2 · Probar siempre' }
];

type Step = 'form' | 'preview' | 'done';

export default function OpensipsEntityModal({
  kind,
  show,
  onRequestClose,
  payload,
  duplicate = false
}: Props) {
  const modalRef = useRef<HTMLDivElement>(null);
  useModal(modalRef, show);
  const meta = META[kind];
  const isEdit = payload != null && !duplicate;
  const id: number | null = isEdit ? Number(payload.id) : null;

  const [step, setStep] = useState<Step>('form');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [before, setBefore] = useState<ColumnView | null>(null);
  const [body, setBody] = useState<any>(null);
  const [result, setResult] = useState<WriteResult | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid }
  } = useForm<FormValues>({ mode: 'onChange', defaultValues: EMPTY });
  const resources = useFieldArray({ control, name: 'resources' });

  useEffect(() => {
    reset(payload ? fromPayload(kind, payload) : EMPTY);
    setStep('form');
    setPreview(null);
    setBefore(null);
    setResult(null);
  }, [payload, show, kind, reset]);

  const cleanBackdrop = () => {
    document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove());
    document.body.classList.remove('modal-open');
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  };
  const close = (refresh: boolean) => {
    onRequestClose(refresh);
    reset(EMPTY);
    cleanBackdrop();
  };

  const errMsg = (error: any) => {
    const m = error?.error ?? error?.status ?? error;
    return typeof m === 'string' ? m : JSON.stringify(m);
  };

  /** Paso 1: dryRun. En edición también pide el preview del registro ORIGINAL para pintar el diff. */
  const doPreview = async (f: FormValues) => {
    try {
      setBusy(true);
      const b = toBody(kind, f);
      setBody(b);
      const a = api[kind];
      if (isEdit && id !== null) {
        const [next, current] = await Promise.all([
          a.previewUpdate(id, b),
          a.previewUpdate(id, toBody(kind, fromPayload(kind, payload)))
        ]);
        setPreview(next);
        setBefore(current.record);
      } else {
        setPreview(await a.previewCreate(b));
        setBefore(null);
      }
      setStep('preview');
    } catch (error: any) {
      AlertService.toastError(errMsg(error) || 'El backend rechazó el preview');
    } finally {
      setBusy(false);
    }
  };

  /** Paso 2: escribir de verdad. El backend dispara el reload y lo reporta. */
  const doApply = async () => {
    try {
      setBusy(true);
      const a = api[kind];
      const res = isEdit && id !== null ? await a.update(id, body) : await a.create(body);
      setResult(res);
      setStep('done');
      LogData(body, `${isEdit ? 'edit' : 'add'}-opensips-${kind}`, window.location.href);
      if (res?.reload?.ok) AlertService.toastSuccess('Guardado y recargado en OpenSIPS');
      else if (res?.reload?.triggered)
        AlertService.toastError('Guardado, pero el reload de OpenSIPS falló');
      else AlertService.toastSuccess('Guardado en la BD de OpenSIPS');
    } catch (error: any) {
      AlertService.toastError(errMsg(error) || 'No se pudo guardar');
    } finally {
      setBusy(false);
    }
  };

  const renderSelect = (
    name: keyof FormValues,
    options: { value: string; label: string }[],
    label: string
  ) => (
    <div className="fv-row mb-5">
      <label className="form-label required">{label}</label>
      <Controller
        control={control}
        name={name}
        rules={{ required: true }}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <>
            <Select
              options={options}
              className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
              classNamePrefix="form-select-custom"
              onChange={(opt: any) => onChange(opt ? opt.value : options[0].value)}
              value={options.find((o) => o.value === String(value)) ?? options[0]}
            />
            <InvalidFeedback error={error} />
          </>
        )}
      />
    </div>
  );

  // ----- fieldsets ----------------------------------------------------------
  const salienteFields = (
    <div className="row g-5">
      <div className="col-md-4">
        <Input
          label="Grupo (group_id)"
          requiredIndicator="required"
          type="number"
          placeholder="10 TELCEL · 20 ATT…"
          errors={errors.groupId}
          {...register('groupId', intRule(0, 4_000_000_000, 'groupId'))}
        />
      </div>
      <div className="col-md-8">
        <Input
          label="URI de destino (dst_uri)"
          requiredIndicator="required"
          placeholder="sip:185.32.76.10:5060"
          errors={errors.dstUri}
          {...register('dstUri', {
            required: 'dstUri es requerido',
            maxLength: { value: 128, message: 'Máximo 128' },
            pattern: { value: /^sips?:.+/, message: 'Debe empezar con sip: o sips:' }
          })}
        />
      </div>
      <div className="col-md-6">{renderSelect('probeMode', PROBE_OPTS, 'Probe mode')}</div>
      <div className="col-md-6">
        <Input
          label="Máx. canales (max_channels)"
          optional
          type="number"
          placeholder="vacío = sin límite"
          errors={errors.maxChannels}
          {...register('maxChannels', intRule(0, 1_000_000, 'maxChannels', false))}
        />
      </div>
      <div className="col-12">
        <label className="form-label required">Recursos / capacidad</label>
        <div className="form-text mb-2">
          Formato OpenSIPS <code>recurso=capacidad;…</code>. Capacidad 0 en todos = salida inactiva.
        </div>
        {resources.fields.map((fld, i) => (
          <div className="d-flex gap-3 align-items-start mb-2" key={fld.id}>
            <div className="flex-grow-1">
              <Input
                placeholder="pstn"
                sizeClass="form-control-sm"
                errors={errors.resources?.[i]?.resource}
                {...register(`resources.${i}.resource` as const, {
                  required: 'requerido',
                  maxLength: { value: 60, message: 'Máximo 60' }
                })}
              />
            </div>
            <div style={{ width: 140 }}>
              <Input
                type="number"
                placeholder="capacidad"
                sizeClass="form-control-sm"
                errors={errors.resources?.[i]?.capacity}
                {...register(
                  `resources.${i}.capacity` as const,
                  intRule(0, 1_000_000, 'capacidad')
                )}
              />
            </div>
            <button
              type="button"
              className="btn btn-icon btn-sm btn-light-danger"
              disabled={resources.fields.length <= 1}
              onClick={() => resources.remove(i)}
            >
              <Icon icon="ki-trash" size="fs-5" />
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-sm btn-light-primary fw-bold"
          onClick={() => resources.append({ resource: '', capacity: '' })}
        >
          <Icon icon="ki-plus" size="fs-5 me-1" /> Agregar recurso
        </button>
      </div>
    </div>
  );

  const entranteFields = (
    <div className="row g-5">
      <div className="col-md-3">
        <Input
          label="Grupo (grp)"
          requiredIndicator="required"
          type="number"
          errors={errors.grp}
          {...register('grp', intRule(0, 65535, 'grp'))}
        />
      </div>
      <div className="col-md-5">
        <Input
          label="IP"
          requiredIndicator="required"
          placeholder="10.12.1.70"
          errors={errors.ip}
          {...register('ip', {
            required: 'IP requerida',
            pattern: {
              value: /^(\d{1,3}\.){3}\d{1,3}$|^[0-9a-fA-F:]+$/,
              message: 'IPv4 o IPv6 válida'
            }
          })}
        />
      </div>
      <div className="col-md-2">
        <Input
          label="Máscara"
          requiredIndicator="required"
          type="number"
          errors={errors.mask}
          {...register('mask', intRule(0, 128, 'mask'))}
        />
      </div>
      <div className="col-md-2">
        <Input
          label="Puerto"
          requiredIndicator="required"
          type="number"
          placeholder="0 = todos"
          errors={errors.port}
          {...register('port', intRule(0, 65535, 'port'))}
        />
      </div>
      <div className="col-md-4">{renderSelect('proto', PROTO_OPTS, 'Protocolo')}</div>
      <div className="col-md-4">
        <Input
          label="Pattern"
          optional
          errors={errors.pattern}
          {...register('pattern', { maxLength: { value: 64, message: 'Máximo 64' } })}
        />
      </div>
      <div className="col-md-4">
        <Input
          label="Context info"
          optional
          errors={errors.contextInfo}
          {...register('contextInfo', { maxLength: { value: 32, message: 'Máximo 32' } })}
        />
      </div>
    </div>
  );

  const dialplanFields = (
    <div className="row g-5">
      <div className="col-md-3">
        <Input
          label="dpid"
          requiredIndicator="required"
          type="number"
          errors={errors.dpid}
          {...register('dpid', intRule(0, 2_147_483_647, 'dpid'))}
        />
      </div>
      <div className="col-md-3">
        <Input
          label="Prioridad (pr)"
          requiredIndicator="required"
          type="number"
          errors={errors.pr}
          {...register('pr', intRule(0, 2_147_483_647, 'pr'))}
        />
      </div>
      <div className="col-md-3">{renderSelect('matchOp', MATCH_OPTS, 'match_op')}</div>
      <div className="col-md-3">
        <Input
          label="match_flags"
          requiredIndicator="required"
          type="number"
          errors={errors.matchFlags}
          {...register('matchFlags', intRule(0, 2_147_483_647, 'matchFlags'))}
        />
      </div>
      <div className="col-12">
        <Input
          label="match_exp"
          requiredIndicator="required"
          placeholder={watch('matchOp') === 'regex' ? '^52(\\d{10})$' : '5255...'}
          errors={errors.matchExp}
          {...register('matchExp', {
            required: 'match_exp es requerido',
            maxLength: { value: 500, message: 'Máximo 500' }
          })}
        />
      </div>
      <div className="col-md-6">
        <Input
          label="subst_exp"
          optional
          placeholder="^52(\\d{10})$"
          errors={errors.substExp}
          {...register('substExp', { maxLength: { value: 64, message: 'Máximo 64' } })}
        />
      </div>
      <div className="col-md-6">
        <Input
          label="repl_exp"
          optional
          placeholder="\\1"
          errors={errors.replExp}
          {...register('replExp', { maxLength: { value: 32, message: 'Máximo 32' } })}
        />
      </div>
      <div className="col-md-6">
        <Input
          label="timerec"
          optional
          errors={errors.timerec}
          {...register('timerec', { maxLength: { value: 255, message: 'Máximo 255' } })}
        />
      </div>
      <div className="col-md-6">
        <Input
          label="attrs"
          optional
          errors={errors.attrs}
          {...register('attrs', { maxLength: { value: 255, message: 'Máximo 255' } })}
        />
      </div>
      <div className="col-12">
        <label className="form-check form-switch form-check-custom form-check-solid gap-3 style-pointer">
          <input
            className="form-check-input w-45px h-25px"
            type="checkbox"
            {...register('enabled')}
          />
          <span className="form-check-label fw-bold text-gray-700 fs-7">Regla habilitada</span>
        </label>
      </div>
    </div>
  );

  const descMax = kind === 'saliente' ? 128 : kind === 'entrante' ? 300 : 500;

  return (
    <div
      className="modal fade show d-block"
      ref={modalRef}
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      data-bs-backdrop="static"
    >
      <div className="modal-dialog modal-dialog-centered mw-900px">
        <div className="modal-content shadow-lg border-0">
          <div className="modal-header justify-content-end border-0 pb-0 pt-4 px-6">
            <button
              type="button"
              className="btn btn-sm btn-icon btn-light-danger rounded-circle"
              onClick={() => close(step === 'done')}
            >
              <i className="ki-duotone ki-cross fs-2">
                <span className="path1" />
                <span className="path2" />
              </i>
            </button>
          </div>

          <div className="modal-body pt-0 pb-10 px-6 px-xl-12">
            <div className="text-center mb-6">
              <h1 className="mb-2 fs-2x">{isEdit ? meta.title : meta.titleNew}</h1>
              <p className="text-muted fw-semibold fs-6 mb-2">{meta.help}</p>
              <div className="d-flex justify-content-center gap-2 fs-8 fw-bold">
                <span className={`badge ${step === 'form' ? 'badge-primary' : 'badge-light'}`}>
                  1 · Datos
                </span>
                <Icon icon="ki-right" size="fs-7 text-gray-400" />
                <span className={`badge ${step === 'preview' ? 'badge-primary' : 'badge-light'}`}>
                  2 · Previsualizar (dryRun)
                </span>
                <Icon icon="ki-right" size="fs-7 text-gray-400" />
                <span className={`badge ${step === 'done' ? 'badge-primary' : 'badge-light'}`}>
                  3 · Aplicado + {meta.reloadCmd}
                </span>
              </div>
              <div className="separator border-2 my-4"></div>
            </div>

            {step === 'form' && (
              <form
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit(doPreview)(e);
                }}
              >
                <Title
                  title={`Tabla ${meta.table}`}
                  subtitle="OpenSIPS 137.184.186.26"
                  icon="ki-data"
                />
                {kind === 'saliente' && salienteFields}
                {kind === 'entrante' && entranteFields}
                {kind === 'dialplan' && dialplanFields}

                <div className="fv-row mt-5">
                  <label className="form-label">Descripción</label>
                  <TexArea
                    rows={2}
                    placeholder="Para qué es este registro…"
                    {...register('description', {
                      maxLength: { value: descMax, message: `Máximo ${descMax}` }
                    })}
                  />
                  <InvalidFeedback error={errors.description} />
                </div>

                <div className="d-flex justify-content-center align-items-center pt-8 gap-3">
                  <button
                    type="button"
                    className="btn btn-light hover-scale fs-7 fw-bold"
                    onClick={() => close(false)}
                  >
                    Cancelar
                  </button>
                  <Button
                    type="submit"
                    className="btn btn-primary hover-scale fs-7 fw-bold"
                    disabled={!isValid}
                    isLoading={busy}
                  >
                    Previsualizar
                    <Icon icon="ki-eye" size="fs-4 ms-2" />
                  </Button>
                </div>
              </form>
            )}

            {step === 'preview' && preview && (
              <>
                <div className="alert alert-warning d-flex align-items-center p-4 mb-5 fs-7">
                  <Icon icon="ki-information-5" size="fs-2 me-3" />
                  Nada se ha escrito todavía. Revisa la fila y confirma; al aplicar, el backend
                  escribe en MySQL y ejecuta <code className="ms-1">{meta.reloadCmd}</code>.
                </div>
                <OpensipsPreview
                  table={meta.table}
                  action={preview.action}
                  record={preview.record}
                  before={before}
                />
                <div className="d-flex justify-content-center align-items-center pt-8 gap-3">
                  <button
                    type="button"
                    className="btn btn-light hover-scale fs-7 fw-bold"
                    onClick={() => setStep('form')}
                    disabled={busy}
                  >
                    <Icon icon="ki-left" size="fs-4 me-1" /> Corregir
                  </button>
                  <Button
                    type="button"
                    className="btn btn-danger hover-scale fs-7 fw-bold"
                    onClick={doApply}
                    isLoading={busy}
                  >
                    {isEdit ? 'Aplicar cambios' : 'Crear registro'} en OpenSIPS
                    <Icon icon="ki-check" size="fs-4 ms-2" />
                  </Button>
                </div>
              </>
            )}

            {step === 'done' && result && (
              <>
                <ReloadBanner
                  reload={result.reload as OpensipsReloadResult}
                  what={meta.reloadCmd}
                />
                <div className="mt-5">
                  <div className="fs-7 fw-bold text-gray-600 mb-2">Registro guardado</div>
                  <pre
                    className="bg-light rounded p-4 fs-8 mb-0"
                    style={{ maxHeight: 260, overflow: 'auto' }}
                  >
                    {JSON.stringify(result.record, null, 2)}
                  </pre>
                </div>
                <div className="d-flex justify-content-center align-items-center pt-8 gap-3">
                  <button
                    type="button"
                    className="btn btn-primary hover-scale fs-7 fw-bold"
                    onClick={() => close(true)}
                  >
                    Cerrar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
